// ============================================================================
// CANCUN100 — MandarATere.js  (APP 2/10 · Comisiones 2.0: el agente manda el folio a revisión)
// ----------------------------------------------------------------------------
// Reglas (Paso 11/12): solo si los tours ya se mandaron a reservar («Primero manda a reservar tus tours»);
// si faltan obligatorios: «Tu cupón está incompleto, le falta X» con «Completar dato» (vuelve al formulario,
// edición sencilla) o «Enviar a Tere a verificación» (crea autorizaciones_admin tipo 'incompleto' y manda igual).
// El ID de Komito se puede completar aquí mismo (va a reservas.komito_conversacion_id).
// ============================================================================
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA } from './tema';
import { Ic } from './iconos'; // APP 2/24: íconos en lugar de emojis
import { itemsDe } from './contabilidadV3';
import { cambiarEtapa, etapaDe, etiquetaEtapa, faltantesParaTere, reservasMandadas, reservasSinMandar, hotelesSinCodigo } from './etapas'; // APP 2/25 (RC-37)

export default function MandarATere({ C, reservaId, folio, quien, onCerrar, onReservar, onCompletar, onListo }) {
  const [r, setR] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [hoteles, setHoteles] = useState([]); // APP 2/25 (RC-37): códigos de confirmación de hotel
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [komito, setKomito] = useState('');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [{ data: a, error: ea }, { data: b }, { data: h }] = await Promise.all([
        supabase.from('reservas').select('*').eq('id', reservaId).maybeSingle(),
        supabase.from('pagos_cliente').select('id, es_deposito_inicial, comprobante_url, comprobante_path, monto, created_at').eq('reserva_id', reservaId),
        supabase.from('hoteles_reserva').select('id, hotel_nombre, codigo_reserva').eq('reserva_id', reservaId),
      ]);
      if (!vivo) return;
      if (ea) setErr(ea.message);
      setR(a || null); setPagos(b || []); setHoteles(h || []); setCargando(false);
    })();
    return () => { vivo = false; };
  }, [reservaId]);

  const items = r ? itemsDe(r) : [];
  const etapa = etapaDe(r);
  // APP 2/25 (RC-37): «Mandar a pago» solo con todas las reservas mandadas, cada hotel con código y el comprobante inicial.
  const reservados = reservasMandadas(items);
  const pendReserva = reservasSinMandar(items);
  const sinCodigo = hotelesSinCodigo(hoteles);
  const faltanBase = r ? faltantesParaTere({ reserva: r, items, pagosCliente: pagos }).filter((f) => !(f.clave === 'cliente.komito' && /^\d+$/.test(komito.trim()))) : [];
  const faltan = [...faltanBase, ...pendReserva.map((it) => ({ clave: `${it.item_id}.reserva`, texto: `«${it.nombre || 'servicio'}» sin mandar a reservar`, grupo: 'Reservas' })), ...sinCodigo.map((h) => ({ clave: `hotel.${h.id}.codigo`, texto: `Código de confirmación del hotel ${h.hotel_nombre || ''}`.trim(), grupo: 'Hoteles' }))];
  const sinComprobante = faltan.some((f) => f.clave === 'pago.comprobante'); // sin comprobante inicial no hay excepción
  const enBolsa = !!etapa && ['en_bolsa', 'reservado', 'regresado'].includes(etapa);
  const puedeMandar = enBolsa && faltan.length === 0;
  const puedeExcepcion = enBolsa && faltan.length > 0 && !sinComprobante;

  const guardarKomito = async () => {
    const k = komito.trim();
    if (!/^\d+$/.test(k)) return true;
    const { error } = await supabase.from('reservas').update({ komito_conversacion_id: Number(k) }).eq('id', reservaId);
    if (error) { setErr('No se guardó el ID de Komito: ' + error.message); return false; }
    return true;
  };
  const enviar = async (conAutorizacion) => {
    setBusy(true); setErr('');
    try {
      if (!(await guardarKomito())) { setBusy(false); return; }
      if (conAutorizacion && faltan.length) {
        if (sinComprobante) throw new Error('Sin el comprobante del pago inicial no hay excepción.');
        const { error: eA } = await supabase.from('autorizaciones_admin').insert({ tipo: 'incompleto', reserva_id: reservaId, detalle: { folio: r.folio, faltantes: faltan.map((f) => f.texto), excepcion: true }, pedido_por: quien || null });
        if (eA) throw eA;
      }
      const { error } = await cambiarEtapa(supabase, { reservaId, a: 'en_revision', por: quien, motivo: conAutorizacion && faltan.length ? `Excepción pedida a Tere · falta: ${faltan.map((f) => f.texto).join(' · ')}` : 'Mandar a pago', exigir: ['en_bolsa', 'reservado', 'regresado'] });
      if (error) throw error;
      if (onListo) onListo();
    } catch (e) { setErr(e.message || String(e)); }
    setBusy(false);
  };

  const grupos = faltan.reduce((m, f) => { (m[f.grupo] = m[f.grupo] || []).push(f); return m; }, {});
  const btn = (txt, onClick, tipo = 'secondary', disabled) => (
    <button type="button" disabled={disabled || busy} onClick={onClick} className={`au-btn au-btn--${tipo}`} style={{ height: 38, padding: '0 14px', fontSize: 14 }}>{txt}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 335, padding: 16, backdropFilter: 'blur(8px)' }} onClick={() => !busy && onCerrar()}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIO_TARJETA, padding: 20, maxWidth: 520, width: '100%', maxHeight: '92vh', overflow: 'auto', boxShadow: 'var(--au-shadow-card)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}><Ic n="Send" chico /> Mandar a pago · {folio || r?.folio}</div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {cargando && <div style={{ color: C.muted, fontSize: 12 }}>Cargando…</div>}
        {err && <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 10 }}>{err}</div>}
        {r && !cargando && (<>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Etapa actual: <strong style={{ color: C.text }}>{etiquetaEtapa(etapa)}</strong>. Se manda solo con todas las reservas mandadas, cada hotel con su código y el comprobante del pago inicial. Tere revisa y, si todo está bien, aprueba y paga.</div>

          {enBolsa && !reservados && (
            <div style={{ padding: '12px 14px', borderRadius: RADIO, background: 'var(--au-yellow-bg)', border: '1px solid var(--au-yellow-bd)', color: C.text, fontSize: 14, marginBottom: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Primero manda a reservar todo (aunque sea tentativo) y luego a pago.</div>
              <div style={{ fontSize: 12, color: C.muted }}>Sin mandar: {pendReserva.map((it) => it.nombre).join(' · ')}.</div>
            </div>
          )}
          {!enBolsa && (
            <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 10 }}>Este folio no está en Mi bolsa (etapa «{etiquetaEtapa(etapa)}»); no se puede mandar a Tere desde aquí.</div>
          )}

          {enBolsa && faltan.length > 0 && (
            <div style={{ padding: '12px 14px', borderRadius: RADIO, background: 'var(--au-error-bg)', border: '1px solid var(--au-error-bd)', marginBottom: 12 }}>
              <div style={{ fontWeight: 800, color: C.text, fontSize: 14, marginBottom: 6 }}>Todavía no se puede mandar a pago, falta:</div>
              {Object.entries(grupos).map(([g, fs]) => (
                <div key={g} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{g}</div>
                  <ul style={{ margin: '2px 0 0', paddingLeft: 18, fontSize: 14, color: C.text }}>{fs.map((f) => <li key={f.clave}>{f.texto}</li>)}</ul>
                </div>
              ))}
              {faltan.some((f) => f.clave === 'cliente.komito') && (
                <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: C.text }}>ID de Komito (número de la conversación)
                  <input value={komito} onChange={(e) => setKomito(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="Ej. 4630" className="au-input" style={{ marginTop: 4, height: 38 }} />
                </label>
              )}
              <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{sinComprobante ? 'Sin el comprobante del pago inicial no hay excepción: súbelo primero.' : 'Puedes completarlo tú, o pedir una excepción: a Tere le sale la lista exacta de lo que falta y aprueba o rechaza con motivo.'}</div>
            </div>
          )}
          {puedeMandar && (
            <div style={{ padding: '12px 14px', borderRadius: RADIO, background: 'var(--au-success-bg)', border: '1px solid var(--au-success-bd)', color: C.text, fontSize: 14, marginBottom: 12 }}><Ic n="CircleCheck" chico /> Cupón completo: datos del cliente, comprobante del depósito y servicios obligatorios.</div>
          )}

          {enBolsa && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {btn('Cancelar', onCerrar, 'ghost')}
              {faltan.length > 0 && !reservados && btn(<><Ic n="Send" chico /> Reservar</>, () => { onCerrar(); if (onReservar) onReservar(); }, 'secondary')}
              {faltan.length > 0 && btn(<><Ic n="Pencil" chico /> Completar dato</>, () => { onCerrar(); if (onCompletar) onCompletar(); }, 'secondary')}
              {faltan.length > 0
                ? btn(busy ? 'Enviando…' : <><Ic n="AlertTriangle" chico /> Pedir excepción a Tere</>, () => enviar(true), 'primary', !puedeExcepcion)
                : btn(busy ? 'Enviando…' : <><Ic n="Send" chico /> Mandar a pago</>, () => enviar(false), 'primary', !puedeMandar)}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
